import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Sheet } from '../components/Sheet.jsx';
import { neueZeile } from '../../api/src/lib/formulare/normalisieren.js';
import { Feld, gemerkt } from './Felder.jsx';
import { alleAktion, fortschritt, kurzinfo, setzen, zeigtFeld, zeileEntfernen, zeileKopieren } from './logik.js';
import { useToast } from '../state/toast.jsx';

/**
 * Schritt-Ansicht für lange Formulare (Vorlage `schritte`, z. B. Messprotokoll): ein Abschnitt pro Bildschirm, oben
 * die Schritte mit Stand, unten «Weiter». Prüfpunkte mit «Alles i. O.» und nur die Ausnahmen antippen; Zeilen einer
 * Tabelle (Stromkreise) im Vollbild mit «Nächster» – kopiert Leitung/Schutz und zählt den Namen hoch.
 */

const leer = (w) => w === '' || w === null || w === undefined || (Array.isArray(w) && !w.length);

/** Schritte einer Vorlage: Kopf, Abschnitte, Schluss. */
export function schritteVon(v) {
  return [
    { key: 'kopf', titel: v.schritte?.kopf ?? 'Allgemein' },
    ...v.abschnitte.map((a) => ({ key: a.id, titel: a.kurz ?? a.titel, a })),
    ...(v.fuss?.length ? [{ key: 'fuss', titel: v.fussTitel ?? 'Abschluss' }] : []),
  ];
}

/** Was in einem Schritt noch fehlt (Pflichtfelder) – für Stand oben und die Liste im Abschluss. */
export function offenIn(v, s, d) {
  if (s.key === 'kopf') return [...(d.titel?.trim() ? [] : [v.titelFeld.label ?? 'Titel']), ...(v.kopf ?? []).filter((f) => f.pflicht && leer(d[f.id])).map((f) => f.titel)];
  if (s.key === 'fuss') return (v.fuss ?? []).filter((f) => f.pflicht && leer(d[f.id])).map((f) => f.titel);
  const a = s.a;
  if (a.art !== 'tabelle') return [];
  const out = [];
  if (a.pflichtZeilen && !d[a.id]?.length) out.push(`mindestens ein${a.zeile ? `e ${a.zeile}` : 'e Zeile'}`);
  for (const z of d[a.id] ?? []) for (const f of a.spalten.filter((x) => x.pflicht)) if (leer(z[f.id])) out.push(`${a.zeile} ${z.nr}: ${f.titel}`);
  return out;
}

/** Kurzer Stand für die Schrittleiste: «12/16», «3», «✓». */
function stand(v, s, d) {
  const offen = offenIn(v, s, d).length;
  if (s.a?.art === 'festeZeilen') {
    // erledigt: jeder Punkt i. O. oder mit Bemerkung (n. i. O. begründet)
    const p = fortschritt(s.a, d);
    const feld = s.a.fortschritt?.feld;
    const erledigt = Object.values(d[s.a.id] ?? {}).every((z) => !feld || z[feld] === true || !!String(z.bemerkung ?? '').trim());
    return { fertig: erledigt, text: `${p.n}/${p.gesamt}` };
  }
  if (s.a?.art === 'tabelle') {
    const n = d[s.a.id]?.length ?? 0;
    return { fertig: n > 0 && !offen, text: String(n), warnung: n > 0 && offen > 0 };
  }
  return { fertig: !offen, text: offen ? `${offen} offen` : '' };
}

// ─── Schrittleiste oben ──────────────────────────────────────────────────────

export function SchrittLeiste({ schritte, aktiv, daten, v, onWahl }) {
  return (
    <nav aria-label="Schritte" className="schritte">
      {schritte.map((s, i, alle) => {
        let st = stand(v, s, daten);
        // Abschluss gilt erst als erledigt, wenn alles andere es ist
        if (s.key === 'fuss') st = { ...st, fertig: st.fertig && alle.every((x) => x.key === 'fuss' || stand(v, x, daten).fertig) };
        const an = aktiv === i;
        return (
          <button key={s.key} type="button" onClick={() => onWahl(i)} aria-current={an ? 'step' : undefined} aria-label={`Schritt ${i + 1}: ${s.titel}${st.fertig ? ', erledigt' : st.text ? `, ${st.text}` : ''}`}
            className={`schritt${an ? ' an' : ''}${st.fertig ? ' fertig' : ''}`}>
            <span className="punkt">{st.fertig && !an ? <Icon name="check" size={14} stroke={3} /> : i + 1}</span>
            <span className="name">{s.titel}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── Kopf / Schluss: Felder im Raster ────────────────────────────────────────

const halb = (f) => ['zahl', 'datum', 'zeit'].includes(f.typ) || (f.typ === 'zeile' && (f.max ?? 999) <= 40);

export function FelderRaster({ felder, daten, team, setzeFeld }) {
  return (
    <div className="feldraster">
      {felder.map((f) => (
        <div key={f.id} className={halb(f) ? '' : 'breit'}>
          <Feld f={f} wert={daten[f.id]} team={team} onChange={setzeFeld(f)} />
        </div>
      ))}
    </div>
  );
}

// ─── Prüfpunkte (feste Zeilen) ───────────────────────────────────────────────

export function PruefSchritt({ a, daten, team, tun }) {
  const p = fortschritt(a, daten);
  const haken = a.spalten.find((f) => f.ort === 'zeile' && f.typ === 'haken');
  const alleAn = p.n === p.gesamt;
  return (
    <section className="stack" style={{ gap: 12 }}>
      <div className="schritttitel">
        <h2 className="h1">{a.titel}</h2>
        <span className="sub">{p.n} von {p.gesamt} {a.fortschritt?.text ?? ''}</span>
        <span className="balken" aria-hidden="true"><span style={{ width: `${p.gesamt ? (100 * p.n) / p.gesamt : 0}%` }} /></span>
      </div>
      {a.aktion?.fuerAlle && (
        <button type="button" className={`btn block${alleAn ? ' secondary' : ''}`} style={{ minHeight: 56, fontSize: 17 }} onClick={() => tun((d, c) => alleAktion(null, d, a, c))}>
          {alleAn ? 'Alle zurücksetzen' : <><Icon name="check" size={20} stroke={3} />{a.aktion.titel}</>}
        </button>
      )}
      {!alleAn && a.aktion?.fuerAlle && <span className="sub" style={{ fontSize: 13, marginTop: -4 }}>Danach nur die Ausnahmen antippen.</span>}
      <ul className="pruefliste" aria-label={a.titel}>
        {a.zeilen.map((zd) => (
          <PruefPunkt key={zd.id} a={a} zd={zd} z={daten[a.id][zd.id]} haken={haken} team={team}
            onSetzen={(feld, wert) => tun((d, c) => setzen(null, d, a, zd.id, feld, wert, c))} />
        ))}
      </ul>
    </section>
  );
}

function PruefPunkt({ a, zd, z, haken, team, onSetzen }) {
  const details = a.spalten.filter((f) => f.ort !== 'zeile' && zeigtFeld(a, f, zd, z));
  const [offen, setOffen] = useState(false);
  const an = haken ? z[haken.id] === true : false;
  const kurz = kurzinfo(a, z);
  const umschalten = () => {
    if (!haken) return;
    onSetzen(haken.id, !an);
    if (an && details.length) setOffen(true); // war i. O., jetzt nicht → gleich begründen
  };
  return (
    <li className={`pruefpunkt${an ? ' an' : ''}`}>
      <div className="row" style={{ gap: 0, alignItems: 'stretch' }}>
        <button type="button" className="pruefknopf" aria-pressed={an} onClick={umschalten} aria-label={`${zd.titel}: ${haken?.titel ?? ''}`}>
          <span className="haken" aria-hidden="true">{an && <Icon name="check" size={18} stroke={3} />}</span>
          <span className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
            <span className="titel">{zd.titel}</span>
            {kurz && !offen && <span className="ellipsis notiz">{kurz}</span>}
          </span>
        </button>
        {details.length > 0 && (
          <button type="button" className="notizknopf" aria-expanded={offen} aria-label={`Bemerkung zu ${zd.titel}`} onClick={() => setOffen((x) => !x)}>
            <Icon name="edit" size={18} />
          </button>
        )}
      </div>
      {offen && (
        <div className="stack" style={{ gap: 8, padding: '0 12px 12px 56px' }}>
          {details.map((f) => (
            <AutoFokus key={f.id}>
              <Feld f={f} wert={z[f.id]} team={team} aria={`${f.titel} ${zd.titel}`} onChange={(w) => onSetzen(f.id, w)} />
            </AutoFokus>
          ))}
        </div>
      )}
    </li>
  );
}

function AutoFokus({ children }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.querySelector('input, textarea')?.focus({ preventScroll: false }); }, []);
  return <div ref={ref}>{children}</div>;
}

// ─── Tabelle (z. B. Stromkreise): Liste + Vollbild pro Zeile ─────────────────

/** «F1» → «F2», «Zone 9» → «Zone 10»; sonst leer. */
export const naechsterName = (name) => {
  const m = String(name ?? '').match(/^(.*?)(\d+)(\D*)$/);
  return m ? `${m[1]}${Number(m[2]) + 1}${m[3]}` : '';
};

export function TabellenSchritt({ a, daten, team, tun }) {
  const zeilen = daten[a.id];
  const [offen, setOffen] = useState(null); // Index der offenen Zeile
  const schliessen = useCallback(() => setOffen(null), []); // stabil – das Sheet setzt sonst bei jeder Eingabe den Fokus neu
  const toast = useToast();
  const kopfFeld = a.spalten.find((f) => f.ort === 'kopf');
  // Auswahl: feste Reihenfolge der Optionen (Knöpfe springen nicht), danach hier und zuletzt verwendete
  const vorschlaege = (f) => [...new Set([...(f.optionen ?? []), ...zeilen.map((z) => z[f.id]).filter(Boolean), ...gemerkt(f)])].slice(0, 8);

  /** Neue Zeile: Kopie der letzten (ohne Messwerte), Name hochgezählt – oder leer. */
  const neu = () => {
    const i = zeilen.length;
    if (i >= a.max) return;
    tun((d) => {
      if (!d[a.id].length) { d[a.id].push({ nr: 1, ...neueZeile(a) }); return d; }
      const vorher = d[a.id][d[a.id].length - 1];
      zeileKopieren(a, d, d[a.id].length - 1);
      if (kopfFeld) d[a.id][d[a.id].length - 1][kopfFeld.id] = naechsterName(vorher[kopfFeld.id]);
      return d;
    });
    setOffen(i);
  };
  const entfernen = (i) => {
    const vorher = structuredClone(zeilen);
    tun((d) => zeileEntfernen(a, d, i));
    setOffen(null);
    toast({ text: `${a.zeile} ${i + 1} entfernt`, undo: () => tun((d) => { d[a.id] = vorher; return d; }) });
  };

  return (
    <section className="stack" style={{ gap: 12 }}>
      <div className="schritttitel">
        <h2 className="h1">{a.titel}</h2>
        <span className="sub">{zeilen.length ? `${zeilen.length} ${a.anzahl ?? a.titel}` : `Noch keine ${a.anzahl ?? a.titel}`}</span>
      </div>
      {zeilen.length > 0 && (
        <ul className="zeilenliste" aria-label={a.titel}>
          {zeilen.map((z, i) => {
            const fehlt = a.spalten.filter((f) => f.pflicht && leer(z[f.id])).length;
            const kurz = kurzinfo(a, z);
            return (
              <li key={z.nr}>
                <button type="button" className="zeilenkarte" onClick={() => setOffen(i)} aria-label={`${a.zeile} ${z.nr} öffnen${kopfFeld && z[kopfFeld.id] ? `: ${z[kopfFeld.id]}` : ''}`}>
                  <span className={`nr${fehlt ? '' : ' ok'}`}>{z.nr}</span>
                  <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                    <span className="ellipsis" style={{ fontWeight: 800, fontSize: 16 }}>{(kopfFeld && z[kopfFeld.id]) || `${a.zeile} ${z.nr}`}{z.ort ? <span style={{ fontWeight: 600, color: 'var(--ink-2)' }}> · {z.ort}</span> : null}</span>
                    <span className="ellipsis sub" style={{ fontSize: 13 }}>{kurz || 'noch leer'}</span>
                  </span>
                  {fehlt > 0 && <span className="fehltpunkt" title={`${fehlt} Pflichtfeld${fehlt > 1 ? 'er' : ''} offen`}>{fehlt}</span>}
                  <Icon name="chevron" size={18} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button type="button" className={`btn block${zeilen.length ? ' secondary' : ''}`} style={{ minHeight: 56, fontSize: 17 }} onClick={neu} disabled={zeilen.length >= a.max}>
        <Icon name="plus" size={20} stroke={2.6} />{zeilen.length ? `${a.zeile} ${zeilen.length + 1}` : `Erste ${a.zeile} erfassen`}
      </button>
      {zeilen.length > 0 && a.kopieren && <span className="sub" style={{ fontSize: 13 }}>Neue {a.zeile} übernimmt Leitung und Schutz der vorigen – nur noch messen.</span>}
      {offen !== null && zeilen[offen] && (
        <ZeilenSheet a={a} i={offen} z={zeilen[offen]} anzahl={zeilen.length} team={team} vorschlaege={vorschlaege}
          onSetzen={(feld, wert) => tun((d, c) => setzen(null, d, a, offen, feld, wert, c))}
          onWechsel={(j) => setOffen(j)} onNeu={neu} onEntfernen={a.einzelnEntfernen ? () => entfernen(offen) : null} onClose={schliessen} />
      )}
    </section>
  );
}

function ZeilenSheet({ a, i, z, anzahl, team, vorschlaege, onSetzen, onWechsel, onNeu, onEntfernen, onClose }) {
  const ref = useRef(null);
  const kopfFeld = a.spalten.find((f) => f.ort === 'kopf');
  const gruppen = a.gruppen ?? [{ titel: '', felder: a.spalten.map((f) => f.id) }];
  const spalte = (id) => a.spalten.find((f) => f.id === id);
  const letzte = i === anzahl - 1;

  // Wechsel zur nächsten Zeile: nach oben, erstes leeres Feld bereit
  useEffect(() => {
    const sheet = ref.current?.closest('.sheet');
    sheet?.scrollTo?.({ top: 0 });
    const erstes = [...(ref.current?.querySelectorAll('input:not([type=checkbox]), textarea') ?? [])].find((el) => !el.value);
    erstes?.focus({ preventScroll: true });
  }, [i]);

  // «Weiter» auf der Tastatur springt ins nächste Feld
  const weiter = (e) => {
    if (e.key !== 'Enter' || e.target.tagName !== 'INPUT') return;
    const felder = [...ref.current.querySelectorAll('input:not([type=checkbox]):not([type=hidden]), textarea')];
    const n = felder.indexOf(e.target);
    if (n >= 0 && felder[n + 1]) { e.preventDefault(); felder[n + 1].focus(); }
  };

  const name = `${a.zeile} ${z.nr}`;
  return (
    <Sheet title={`${name}${kopfFeld && z[kopfFeld.id] ? ` · ${z[kopfFeld.id]}` : ''}`} onClose={onClose}>
      <div ref={ref} className="stack gruppen" onKeyDown={weiter}>
        {gruppen.map((g) => (
          <fieldset key={g.titel || 'alle'} className="gruppe">
            {g.titel && <legend>{g.titel}</legend>}
            <div className="feldraster">
              {g.felder.map(spalte).filter(Boolean).map((f) => (
                <div key={f.id} className={halb(f) || f.typ === 'zahl' ? '' : 'breit'}>
                  <Feld f={f} wert={z[f.id]} team={team} vorschlaege={vorschlaege(f)} aria={`${f.titel} ${name}`} onChange={(w) => onSetzen(f.id, w)} />
                </div>
              ))}
            </div>
          </fieldset>
        ))}
        {onEntfernen && (
          <button type="button" className="btn secondary entfernen" onClick={onEntfernen}>
            <Icon name="trash" size={18} />{name} entfernen
          </button>
        )}
      </div>
      <div className="sheetfuss">
        <button type="button" className="btn secondary" style={{ minHeight: 52, padding: '0 14px' }} onClick={() => onWechsel(i - 1)} disabled={i === 0} aria-label={`Vorige ${a.zeile}`}>
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="chevron" size={18} /></span>
        </button>
        <button type="button" className="btn secondary" style={{ minHeight: 52, flex: 1 }} onClick={onClose}>Fertig</button>
        <button type="button" className="btn" style={{ minHeight: 52, flex: 1.6 }} onClick={() => (letzte ? onNeu() : onWechsel(i + 1))} disabled={letzte && anzahl >= a.max}
          aria-label={letzte ? `Neue ${a.zeile} ${anzahl + 1}` : `Nächste ${a.zeile}`}>
          {letzte ? <><Icon name="plus" size={18} stroke={2.6} />Neue</> : <>Nächste<Icon name="chevron" size={18} /></>}
        </button>
      </div>
    </Sheet>
  );
}
